// Decide con qué tipo de cambio arranca el Pricer Datec.

/**
 * Rango sano para el USD/BOB. El mismo rango vive por duplicado en el backend
 * (`bcb-exchange-rate.service.ts`), pero no hay ninguna sincronía garantizada
 * entre los dos — cada uno valida una entrada distinta:
 *
 * - El backend valida lo que scrapea de `api.factura.bo` antes de guardarlo.
 * - Este guard valida el `oficial.rate` que llega por HTTP al pricer, cuyo
 *   origen puede ser una tasa cargada A MANO en Datos Maestros (el endpoint
 *   cae a cualquier fuente si no hay ninguna del BCB). Un typo ahí nunca pasa
 *   por el validador del backend porque no se está scrapeando nada — este es
 *   el único guard que lo detiene antes de precargarse en una cotización.
 *
 * Si algún día cambia uno, revisar si corresponde cambiar el otro a mano.
 */
const MIN_TC_PLAUSIBLE = 5;
const MAX_TC_PLAUSIBLE = 40;

export function esTcPlausible(rate: number | null): rate is number {
  return (
    rate !== null && Number.isFinite(rate) && rate >= MIN_TC_PLAUSIBLE && rate <= MAX_TC_PLAUSIBLE
  );
}

/**
 * Entre un valor guardado y una referencia externa, decide cuál usar: la
 * referencia si es creíble, el guardado si no.
 *
 * Esta función NO sabe si la cotización es nueva o ya existente — a propósito.
 * Esa decisión (si corresponde siquiera considerar la referencia) es la regla
 * más importante de todo el pricer: una cotización YA GUARDADA nunca debe ver
 * su TC pisado, porque cambiaría retroactivamente un precio que un cliente ya
 * recibió. Esa regla vive, estructuralmente, en el call site
 * (`pricer-datec-page.tsx`, rama `if (id)` del `useEffect` de carga): esta
 * función directamente no se llama ahí. Antes había acá un parámetro booleano
 * `esCotizacionExistente` que duplicaba esa regla en un `if` — un test la
 * cubría, pero el único call site real la hardcodeaba en `false`, así que un
 * refactor que unificara las dos ramas del `useEffect` podía romper la regla
 * sin que ningún test se enterara. Ver el comentario en esa rama del
 * `useEffect` antes de tocar esto.
 */
export function tcConReferencia(opts: { guardado: number; referencia: number | null }): number {
  return esTcPlausible(opts.referencia) ? opts.referencia : opts.guardado;
}

/**
 * Cambia el TC oficial de los params. Sólo eso: desde F3 (OBS-027) los factores USA/Bolivia no se muestran ni
 * se arrastran; los que trae una cotización vieja se conservan tal cual y los completa el servidor.
 */
export function conTcOficial<T extends { tcOficial: number }>(params: T, nuevoTc: number): T {
  return { ...params, tcOficial: nuevoTc };
}
